import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  UserIcon, 
  PlusIcon, 
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  TruckIcon,
  CalendarIcon,
  DocumentIcon,
  PhoneIcon,
  CurrencyDollarIcon,
  CheckIcon,
  XMarkIcon,
  CalculatorIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { driversService } from '../services/logistics/drivers';
import { vehiclesService } from '../services/logistics/vehicles';
import { externalDriversService } from '../services/logistics/external-drivers';
import { shipmentsService } from '../services/logistics/shipments';
import { shipmentCostingsService } from '../services/logistics/shipment-costings';
import { externalDriverPaymentsService } from '../services/logistics/external-driver-payments';
import { internalDriverPaymentsService } from '../services/logistics/internal-driver-payments';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import DriverStatementModal from '../components/drivers/DriverStatementModal';
import ShipmentCosting from './ShipmentCosting';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';

// Pull the commission a costing record actually represents.
// Prefer the enhanced field, fall back to legacy.
const getCommission = (costing) => {
  if (!costing) return 0;
  return Number(costing.commissionAmount || costing.commission || 0);
};

const toMillis = (v) => {
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Date.parse(v);
    return Number.isNaN(n) ? 0 : n;
  }
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (v.seconds) return v.seconds * 1000;
  return 0;
};

const getShipmentDateMs = (s) =>
  toMillis(s.pickupDate) ||
  toMillis(s.shipmentDate) ||
  toMillis(s.createdAt) ||
  0;

const Drivers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentCompany } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCostingModal, setShowCostingModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [driverBusyMap, setDriverBusyMap] = useState(new Map());
  // driverId -> remaining balance (commission - paid) for internal drivers
  const [internalBalanceMap, setInternalBalanceMap] = useState(new Map());
  
  // External drivers state
  const [activeTab, setActiveTab] = useState('internal'); // 'internal' or 'external'
  const [externalDrivers, setExternalDrivers] = useState([]);
  const [externalDriversLoading, setExternalDriversLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverShipments, setDriverShipments] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [showAllShipments, setShowAllShipments] = useState(false);
  // shipmentId -> commission amount (from shipmentCostings.commissionAmount)
  const [commissionByShipment, setCommissionByShipment] = useState(new Map());
  // driverId -> { unpaidTrips, unpaidCommission, paidCommission, totalTrips, lastActivityMs }
  const [driverStats, setDriverStats] = useState(new Map());
  // Ledger state (per selected driver)
  const [driverPayments, setDriverPayments] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentDate: new Date().toISOString().slice(0, 10),
    method: 'cash',
    notes: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [editingCommissionId, setEditingCommissionId] = useState(null);
  const [editingCommissionValue, setEditingCommissionValue] = useState('');
  const [savingCommission, setSavingCommission] = useState(false);
  const [showExternalDriverModal, setShowExternalDriverModal] = useState(false);
  const [editingExternalDriver, setEditingExternalDriver] = useState(null);
  const [externalDriverForm, setExternalDriverForm] = useState({ name: '', phone: '' });
  const [savingExternalDriver, setSavingExternalDriver] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    nickname: '',
    email: '',
    phone: '',
    licenseNumber: '',
    licenseExpiry: '',
    iqamahNumber: '',
    iqamahExpiry: '',
    healthInsuranceNumber: '',
    healthInsuranceExpiry: '',
    favoriteVehicleId: '',
    status: 'active',
    notes: ''
  });

  const loadDrivers = useCallback(async () => {
    try {
      const driversData = await driversService.getDrivers(currentCompany.id);
      
      // Assign codes to drivers missing them — in parallel
      const driversNeedingCodes = driversData.filter(driver => !driver.driverCode);
      if (driversNeedingCodes.length > 0) {
        await Promise.all(driversNeedingCodes.map(async (driver) => {
          try {
            const driverCode = await driversService.generateDriverCode(currentCompany.id);
            await driversService.updateDriver(driver.id, { driverCode });
            driver.driverCode = driverCode;
          } catch (error) {
            console.error(`Failed to assign code to driver ${driver.id}:`, error);
          }
        }));
      }
      
      setDrivers(driversData);

      // Build busy map from active shipments
      try {
        const shipmentsData = await shipmentsService.getShipments(currentCompany.id);
        const busyMap = new Map();
        shipmentsData.forEach(s => {
          if (s.status === 'delivered' || s.status === 'canceled') return;
          if (s.driverId && !s.isExternalDriver) {
            busyMap.set(s.driverId, { shipmentStatus: s.status, shipmentNumber: s.shipmentNumber || s.trackingNumber });
          }
        });
        setDriverBusyMap(busyMap);
      } catch { /* non-critical — busy map stays empty */ }
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error(t('drivers.failed_to_load', 'Failed to load drivers'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, t]);

  const loadVehicles = useCallback(async () => {
    try {
      const vehiclesData = await vehiclesService.getVehicles(currentCompany.id);
      setVehicles(vehiclesData);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  }, [currentCompany?.id]);

  const loadExternalDrivers = useCallback(async () => {
    if (!currentCompany?.id) return;

    setExternalDriversLoading(true);
    try {
      const [driversData, shipmentsData, costingsData] = await Promise.all([
        externalDriversService.getExternalDrivers(currentCompany.id),
        shipmentsService.getShipments(currentCompany.id),
        shipmentCostingsService.getShipmentCostings(currentCompany.id)
      ]);

      // Build shipmentId -> commission map from costings
      const commissionMap = new Map();
      costingsData.forEach(c => {
        if (c.shipmentId) commissionMap.set(c.shipmentId, getCommission(c));
      });

      // Aggregate per-driver stats from external-driver shipments only
      const stats = new Map();
      shipmentsData.forEach(s => {
        if (!s.isExternalDriver || !s.externalDriverId) return;
        const id = s.externalDriverId;
        const cur = stats.get(id) || {
          unpaidTrips: 0,
          unpaidCommission: 0,
          paidCommission: 0,
          totalTrips: 0,
          lastActivityMs: 0
        };
        const commission = commissionMap.get(s.id) || 0;
        cur.totalTrips += 1;
        if (s.paymentStatus === 'paid') {
          cur.paidCommission += commission;
        } else {
          cur.unpaidTrips += 1;
          cur.unpaidCommission += commission;
        }
        const dateMs = getShipmentDateMs(s);
        if (dateMs > cur.lastActivityMs) cur.lastActivityMs = dateMs;
        stats.set(id, cur);
      });

      setCommissionByShipment(commissionMap);
      setDriverStats(stats);
      setExternalDrivers(driversData);
    } catch (error) {
      console.error('Error loading external drivers:', error);
      toast.error(`Failed to load external drivers: ${error.message}`);
    } finally {
      setExternalDriversLoading(false);
    }
  }, [currentCompany?.id]);

  const loadDriverPayments = useCallback(async (driverId) => {
    if (!currentCompany?.id) return;
    setLedgerLoading(true);
    try {
      const payments = await externalDriverPaymentsService.getPaymentsForDriver(currentCompany.id, driverId);
      setDriverPayments(payments);
    } catch (error) {
      console.error('Error loading driver payments:', error);
      toast.error('Failed to load payment history');
    } finally {
      setLedgerLoading(false);
    }
  }, [currentCompany?.id]);

  const loadDriverShipments = useCallback(async (driverId, isExternal = false) => {
    if (!currentCompany?.id) return;
    
    setShipmentsLoading(true);
    try {
      const shipmentsData = await shipmentsService.getShipments(currentCompany.id);
      const driverShipments = shipmentsData.filter(shipment => {
        if (isExternal) {
          return shipment.isExternalDriver && shipment.externalDriverId === driverId;
        } else {
          return !shipment.isExternalDriver && shipment.driverId === driverId;
        }
      });
      setDriverShipments(driverShipments);
    } catch (error) {
      console.error('Error loading driver shipments:', error);
      toast.error('Failed to load shipments');
    } finally {
      setShipmentsLoading(false);
    }
  }, [currentCompany?.id]);

  const loadAllExternalShipments = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    setShipmentsLoading(true);
    try {
      const shipmentsData = await shipmentsService.getShipments(currentCompany.id);
      const externalShipments = shipmentsData.filter(shipment => shipment.isExternalDriver);
      setDriverShipments(externalShipments);
    } catch (error) {
      console.error('Error loading external shipments:', error);
      toast.error('Failed to load shipments');
    } finally {
      setShipmentsLoading(false);
    }
  }, [currentCompany?.id]);

  const loadInternalBalances = useCallback(async () => {
    if (!currentCompany?.id) return;
    try {
      const [shipmentsData, paymentsData] = await Promise.all([
        shipmentsService.getShipments(currentCompany.id),
        internalDriverPaymentsService.getAllPayments(currentCompany.id),
      ]);
      const earnedMap = new Map();
      shipmentsData.forEach(s => {
        if (s.isExternalDriver) return;
        if (!s.driverId) return;
        const cur = earnedMap.get(s.driverId) || 0;
        earnedMap.set(s.driverId, cur + (Number(s.commissionAmount) || 0));
      });
      const paidMap = new Map();
      paymentsData.forEach(p => {
        if (!p.driverId) return;
        const cur = paidMap.get(p.driverId) || 0;
        paidMap.set(p.driverId, cur + (Number(p.amount) || 0));
      });
      const balance = new Map();
      const ids = new Set([...earnedMap.keys(), ...paidMap.keys()]);
      ids.forEach(id => {
        balance.set(id, (earnedMap.get(id) || 0) - (paidMap.get(id) || 0));
      });
      setInternalBalanceMap(balance);
    } catch (err) {
      console.error('Failed to load internal balances:', err);
    }
  }, [currentCompany?.id]);

  useEffect(() => {
    if (currentCompany?.id) {
      loadDrivers();
      loadVehicles();
      if (activeTab === 'external') {
        loadExternalDrivers();
      }
      if (activeTab === 'internal') {
        loadInternalBalances();
      }
    }
  }, [currentCompany?.id, activeTab, loadDrivers, loadVehicles, loadExternalDrivers, loadInternalBalances]);

  const handleSaveCommission = async (shipmentId, driverId) => {
    const amt = Number(editingCommissionValue);
    if (Number.isNaN(amt) || amt < 0) {
      toast.error(t('drivers.invalid_amount', 'Enter a valid amount'));
      return;
    }
    setSavingCommission(true);
    try {
      await shipmentsService.updateShipment(shipmentId, { commissionAmount: amt });

      const existingCosting = await shipmentCostingsService.getCostingByShipment(shipmentId);
      if (existingCosting?.id) {
        await shipmentCostingsService.updateCostingWithoutValidation(existingCosting.id, {
          commissionAmount: amt,
          commission: amt,
        });
      } else {
        await shipmentCostingsService.addCosting({
          shipmentId,
          companyId: currentCompany.id,
          driverId,
          commissionAmount: amt,
          commission: amt,
        });
      }

      setCommissionByShipment(prev => {
        const next = new Map(prev);
        next.set(shipmentId, amt);
        return next;
      });
      setEditingCommissionId(null);
      setEditingCommissionValue('');
      toast.success(t('drivers.commission_saved', 'Commission saved'));
      if (activeTab === 'external') {
        loadExternalDrivers();
      } else {
        loadInternalBalances();
      }
    } catch (err) {
      console.error('Failed to save commission:', err);
      toast.error(`${t('drivers.commission_save_failed', 'Failed to save commission')}: ${err?.message || err}`);
    } finally {
      setSavingCommission(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      toast.error(t('common.required_field'));
      return;
    }

    if (!currentCompany?.id) {
      toast.error(t('common.company_not_loaded', 'Company information is not available.'));
      return;
    }

    try {
      const driverData = {
        ...formData,
        companyId: currentCompany.id
      };

      if (editingDriver) {
        await driversService.updateDriver(editingDriver.id, driverData);
        toast.success(t('drivers.driver_updated_success'));
      } else {
        await driversService.addDriver(driverData);
        toast.success(t('drivers.driver_created_success'));
      }

      setIsModalOpen(false);
      setEditingDriver(null);
      resetForm();
      loadDrivers();
    } catch (error) {
      console.error('Error saving driver:', error);
      toast.error(t('drivers.failed_to_save', 'Failed to save driver'));
    }
  };

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      name: driver.name || '',
      nickname: driver.nickname || '',
      email: driver.email || '',
      phone: driver.phone || '',
      licenseNumber: driver.licenseNumber || '',
      licenseExpiry: driver.licenseExpiry || '',
      iqamahNumber: driver.iqamahNumber || '',
      iqamahExpiry: driver.iqamahExpiry || '',
      healthInsuranceNumber: driver.healthInsuranceNumber || '',
      healthInsuranceExpiry: driver.healthInsuranceExpiry || '',
      favoriteVehicleId: driver.favoriteVehicleId || '',
      status: driver.status || 'active',
      notes: driver.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (driverId) => {
    if (window.confirm(t('drivers.delete_confirmation', 'Are you sure you want to delete this driver?'))) {
      try {
        await driversService.deleteDriver(driverId);
        toast.success(t('drivers.driver_deleted_success'));
        loadDrivers();
      } catch (error) {
        console.error('Error deleting driver:', error);
        toast.error(t('drivers.failed_to_delete', 'Failed to delete driver'));
      }
    }
  };

  const handleExternalDriverEdit = (driver) => {
    setEditingExternalDriver(driver);
    setExternalDriverForm({ name: driver.name || '', phone: driver.phone || '' });
    setShowExternalDriverModal(true);
  };

  const handleExternalDriverSave = async (e) => {
    e.preventDefault();
    if (!editingExternalDriver) return;
    const name = externalDriverForm.name.trim();
    const phone = externalDriverForm.phone.trim();
    if (!name) {
      toast.error(t('drivers.name_required', 'Name is required'));
      return;
    }
    const isNew = !editingExternalDriver.id;
    if (isNew && !phone) {
      toast.error(t('drivers.phone_required', 'Phone is required for new external drivers'));
      return;
    }
    try {
      setSavingExternalDriver(true);
      if (isNew) {
        if (!currentCompany?.id) throw new Error('No company selected');
        const existing = await externalDriversService.findDriverByPhone(currentCompany.id, phone);
        if (existing) {
          toast.error(t('drivers.duplicate_phone', 'A driver with this phone already exists'));
          return;
        }
        await externalDriversService.addOrUpdateExternalDriver(currentCompany.id, { name, phone });
        toast.success(t('drivers.driver_added_success', 'Driver added'));
      } else {
        await externalDriversService.updateExternalDriver(editingExternalDriver.id, { name, phone });
        toast.success(t('drivers.driver_updated_success', 'Driver updated'));
      }
      setShowExternalDriverModal(false);
      setEditingExternalDriver(null);
      await loadExternalDrivers();
    } catch (error) {
      console.error('Error saving external driver:', error);
      toast.error(t('drivers.failed_to_save', 'Failed to save driver'));
    } finally {
      setSavingExternalDriver(false);
    }
  };

  const handleExternalDriverDelete = async (driver) => {
    if (driver?.stats?.totalTrips > 0) {
      toast.error(t('drivers.cannot_delete_with_trips', 'Cannot delete a driver with recorded trips.'));
      return;
    }
    if (!window.confirm(t('drivers.delete_confirmation', 'Are you sure you want to delete this driver?'))) return;
    try {
      await externalDriversService.deleteExternalDriver(driver.id);
      toast.success(t('drivers.driver_deleted_success', 'Driver deleted'));
      if (selectedDriver?.id === driver.id) setSelectedDriver(null);
      await loadExternalDrivers();
    } catch (error) {
      console.error('Error deleting external driver:', error);
      toast.error(t('drivers.failed_to_delete', 'Failed to delete driver'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      nickname: '',
      email: '',
      phone: '',
      licenseNumber: '',
      licenseExpiry: '',
      iqamahNumber: '',
      iqamahExpiry: '',
      healthInsuranceNumber: '',
      healthInsuranceExpiry: '',
      favoriteVehicleId: '',
      status: 'active',
      notes: ''
    });
  };

  const getExpirationStatus = (expiryDate) => {
    if (!expiryDate) return null;
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', days: Math.abs(daysUntilExpiry), color: 'text-red-600' };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring', days: daysUntilExpiry, color: 'text-yellow-600' };
    } else {
      return { status: 'valid', days: daysUntilExpiry, color: 'text-green-600' };
    }
  };

  const filteredDrivers = drivers
    .filter(driver =>
      driver.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.licenseNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.phone?.includes(searchTerm)
    )
    .sort((a, b) => {
      const score = (d) => {
        if (d.status === 'inactive') return 3;
        if (driverBusyMap.has(d.id)) return 1;
        return 0;
      };
      return score(a) - score(b);
    });

  const filteredExternalDrivers = externalDrivers
    .filter(driver =>
      driver.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.phone?.includes(searchTerm)
    )
    .map(driver => ({
      ...driver,
      stats: driverStats.get(driver.id) || {
        unpaidTrips: 0,
        unpaidCommission: 0,
        paidCommission: 0,
        totalTrips: 0,
        lastActivityMs: 0
      }
    }))
    .sort((a, b) => {
      // Drivers with unpaid balance first
      const aHasUnpaid = a.stats.unpaidTrips > 0 ? 1 : 0;
      const bHasUnpaid = b.stats.unpaidTrips > 0 ? 1 : 0;
      if (aHasUnpaid !== bHasUnpaid) return bHasUnpaid - aHasUnpaid;
      // Then by recency of activity (most recent first)
      if (b.stats.lastActivityMs !== a.stats.lastActivityMs) {
        return b.stats.lastActivityMs - a.stats.lastActivityMs;
      }
      // Tiebreak by name
      return (a.name || '').localeCompare(b.name || '');
    });

  const totalOutstanding = filteredExternalDrivers.reduce(
    (sum, d) => sum + d.stats.unpaidCommission,
    0
  );
  const driversWithDues = filteredExternalDrivers.filter(d => d.stats.unpaidTrips > 0).length;

  const formatRelativeDate = (ms) => {
    if (!ms) return t('drivers.no_activity', 'No activity yet');
    const days = Math.floor((Date.now() - ms) / 86400000);
    if (days <= 0) return t('drivers.today', 'Today');
    if (days === 1) return t('drivers.yesterday', 'Yesterday');
    if (days < 7) return `${days} ${t('drivers.days_ago', 'days ago')}`;
    return new Date(ms).toLocaleDateString();
  };

  // Handle payment status updates
  const updatePaymentStatus = async (shipmentIds, status) => {
    try {
      const updatePromises = shipmentIds.map(shipmentId =>
        shipmentsService.updateShipment(shipmentId, { paymentStatus: status })
      );
      await Promise.all(updatePromises);
      
      toast.success(t('drivers.payment_status_updated', 'Payment status updated successfully'));

      // Reload shipments and per-driver aggregates
      if (selectedDriver) {
        loadDriverShipments(selectedDriver.id, true);
      } else if (showAllShipments) {
        loadAllExternalShipments();
      }
      loadExternalDrivers();

      setSelectedShipments([]);
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
    }
  };

  const handleShipmentSelect = (shipmentId) => {
    setSelectedShipments(prev => 
      prev.includes(shipmentId) 
        ? prev.filter(id => id !== shipmentId)
        : [...prev, shipmentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedShipments.length === driverShipments.length) {
      setSelectedShipments([]);
    } else {
      setSelectedShipments(driverShipments.map(s => s.id));
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('drivers.title')}</h1>
          <p className="text-gray-600">{t('drivers.subtitle', 'Manage your company drivers and track document expirations')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCostingModal(true)}
            className="btn btn-outline flex items-center gap-2"
            title={t('accounting.shipment_costing', 'Shipment Costing')}
          >
            <CalculatorIcon className="h-5 w-5" />
            <span>{t('accounting.shipment_costing', 'Shipment Costing')}</span>
          </button>
          {activeTab === 'internal' && (
            <button
              onClick={() => {
                resetForm();
                setEditingDriver(null);
                setIsModalOpen(true);
              }}
              className="btn-primary flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>{t('drivers.add_driver')}</span>
            </button>
          )}
          {activeTab === 'external' && !selectedDriver && !showAllShipments && (
            <button
              onClick={() => {
                setEditingExternalDriver({ id: null, name: '', phone: '' });
                setExternalDriverForm({ name: '', phone: '' });
                setShowExternalDriverModal(true);
              }}
              className="btn-primary flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>{t('drivers.add_external_driver', 'Add External Driver')}</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('internal');
              setSelectedDriver(null);
              setShowAllShipments(false);
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'internal'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('drivers.internal_drivers', 'Internal Drivers')}
          </button>
          <button
            onClick={() => {
              setActiveTab('external');
              setSelectedDriver(null);
              setShowAllShipments(false);
              loadExternalDrivers();
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'external'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('drivers.external_drivers', 'External Drivers')}
          </button>
        </nav>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'internal' ? (
        <>
          {/* Search and Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="md:col-span-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('drivers.search_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>
            
            <div className="stats-card">
              <div className="flex items-center">
                <UserIcon className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">{t('drivers.total_drivers')}</p>
                  <p className="text-2xl font-bold">{drivers.length}</p>
                </div>
              </div>
            </div>

            <div className="stats-card">
              <div className="flex items-center">
                <UserIcon className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">{t('drivers.active_drivers')}</p>
                  <p className="text-2xl font-bold">{drivers.filter(d => d.status === 'active').length}</p>
                </div>
              </div>
            </div>

            <div className="stats-card">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">{t('drivers.expiring_documents')}</p>
                  <p className="text-2xl font-bold">
                    {drivers.filter(driver => {
                      const licenseStatus = getExpirationStatus(driver.licenseExpiry);
                      const iqamahStatus = getExpirationStatus(driver.iqamahExpiry);
                      const insuranceStatus = getExpirationStatus(driver.healthInsuranceExpiry);
                      return (licenseStatus?.status === 'expiring' || licenseStatus?.status === 'expired') ||
                             (iqamahStatus?.status === 'expiring' || iqamahStatus?.status === 'expired') ||
                             (insuranceStatus?.status === 'expiring' || insuranceStatus?.status === 'expired');
                    }).length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* External Drivers View */}
          {!selectedDriver && !showAllShipments ? (
            <>
              {/* Stats row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="stats-card">
                  <div className="flex items-center">
                    <UserIcon className="h-8 w-8 text-purple-600" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600">{t('drivers.external_drivers', 'External Drivers')}</p>
                      <p className="text-2xl font-bold">{externalDrivers.length}</p>
                    </div>
                  </div>
                </div>
                <div className="stats-card">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-8 w-8 text-amber-600" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600">{t('drivers.drivers_with_dues', 'Drivers with outstanding payments')}</p>
                      <p className="text-2xl font-bold">{driversWithDues}</p>
                    </div>
                  </div>
                </div>
                <div className="stats-card">
                  <div className="flex items-center">
                    <CurrencyDollarIcon className="h-8 w-8 text-red-600" />
                    <div className="ml-4">
                      <p className="text-sm text-gray-600">{t('drivers.total_outstanding', 'Total commission owed')}</p>
                      <p className="text-2xl font-bold">SAR {totalOutstanding.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search + actions */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="relative flex-1 max-w-md">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('drivers.search_drivers', 'Search by name or phone...')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-field pl-10"
                  />
                </div>
                <button
                  onClick={() => {
                    setShowAllShipments(true);
                    loadAllExternalShipments();
                  }}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <DocumentIcon className="h-5 w-5" />
                  <span>{t('drivers.all_shipments', 'All Shipments')}</span>
                </button>
              </div>

              {/* Cards grid */}
              {externalDriversLoading ? (
                <LoadingSpinner />
              ) : filteredExternalDrivers.length === 0 ? (
                <div className="card">
                  <div className="card-body text-center py-12">
                    <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      {t('drivers.no_external_drivers', 'No external drivers found')}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {t('drivers.no_external_drivers_hint', 'External drivers appear here once you assign them to a shipment.')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredExternalDrivers.map((driver) => {
                    const { unpaidTrips, unpaidCommission, paidCommission, totalTrips, lastActivityMs } = driver.stats;
                    const hasUnpaid = unpaidTrips > 0;
                    return (
                      <div
                        key={driver.id}
                        className={`bg-white rounded-lg border ${hasUnpaid ? 'border-amber-300 ring-1 ring-amber-100' : 'border-gray-200'} p-5 hover:shadow-md transition-shadow flex flex-col`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center min-w-0">
                            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                              <UserIcon className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="ml-3 min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">{driver.name}</div>
                              <div className="flex items-center text-xs text-gray-500 mt-0.5">
                                <PhoneIcon className="h-3.5 w-3.5 mr-1" />
                                <span className="truncate">{driver.phone || '—'}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            {hasUnpaid && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                {unpaidTrips} {t('drivers.unpaid', 'unpaid')}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleExternalDriverEdit(driver); }}
                              className="p-1 text-gray-400 hover:text-blue-600"
                              title={t('common.edit', 'Edit')}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleExternalDriverDelete(driver); }}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title={t('common.delete', 'Delete')}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-3">
                          <div className="bg-gray-50 rounded-md px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-gray-500">
                              {t('drivers.unpaid_trips', 'Unpaid trips')}
                            </p>
                            <p className={`text-lg font-bold ${hasUnpaid ? 'text-amber-700' : 'text-gray-900'}`}>
                              {unpaidTrips}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-md px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-gray-500">
                              {t('drivers.amount_owed', 'Amount owed')}
                            </p>
                            <p className={`text-lg font-bold ${hasUnpaid ? 'text-red-600' : 'text-gray-900'}`}>
                              SAR {unpaidCommission.toFixed(2)}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-md px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-gray-500">
                              {t('drivers.total_trips', 'Total trips')}
                            </p>
                            <p className="text-lg font-bold text-gray-900">{totalTrips}</p>
                          </div>
                          <div className="bg-gray-50 rounded-md px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-gray-500">
                              {t('drivers.paid_to_date', 'Paid to date')}
                            </p>
                            <p className="text-lg font-bold text-green-600">SAR {paidCommission.toFixed(2)}</p>
                          </div>
                        </div>

                        <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                          <span className="inline-flex items-center">
                            <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                            {t('drivers.last_activity', 'Last activity')}: {formatRelativeDate(lastActivityMs)}
                          </span>
                        </div>

                        <button
                          onClick={() => {
                            setSelectedDriver(driver);
                            loadDriverShipments(driver.id, true);
                            loadDriverPayments(driver.id);
                          }}
                          className="mt-4 w-full btn-primary text-sm"
                        >
                          {t('drivers.open_ledger', 'Open Ledger')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Driver Details or All Shipments View */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => {
                      setSelectedDriver(null);
                      setShowAllShipments(false);
                      setSelectedShipments([]);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ← {t('common.back', 'Back')}
                  </button>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedDriver ? (
                        <>
                          {selectedDriver.name}
                          <span className="text-sm font-normal text-gray-500 ml-2">({selectedDriver.phone})</span>
                        </>
                      ) : (
                        t('drivers.all_shipments', 'All External Driver Shipments')
                      )}
                    </h2>
                  </div>
                </div>
                
                {selectedDriver && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowStatementModal(true)}
                      className="btn btn-secondary flex items-center gap-2"
                    >
                      <DocumentArrowDownIcon className="h-5 w-5" />
                      <span>{t('drivers.statement', 'Statement')}</span>
                    </button>
                    <button
                      onClick={() => {
                        setPaymentForm({
                          amount: '',
                          paymentDate: new Date().toISOString().slice(0, 10),
                          method: 'cash',
                          notes: ''
                        });
                        setShowPaymentModal(true);
                      }}
                      className="btn-primary flex items-center gap-2"
                    >
                      <CurrencyDollarIcon className="h-5 w-5" />
                      <span>{t('drivers.record_payment', 'Record Payment')}</span>
                    </button>
                  </div>
                )}

                {!selectedDriver && selectedShipments.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      {selectedShipments.length} {t('drivers.select_shipments', 'selected')}
                    </span>
                    <button
                      onClick={() => updatePaymentStatus(selectedShipments, 'paid')}
                      className="btn-success text-sm"
                    >
                      {t('drivers.mark_as_paid', 'Mark as Paid')}
                    </button>
                    <button
                      onClick={() => updatePaymentStatus(selectedShipments, 'not_paid')}
                      className="btn-secondary text-sm"
                    >
                      {t('drivers.mark_as_not_paid', 'Mark as Not Paid')}
                    </button>
                  </div>
                )}
              </div>

              {selectedDriver && (() => {
                // Build ledger entries (chronological) with running balance.
                // Trips = credits (driver earns), payments = debits (we pay).
                const dayKey = (ms) => {
                  if (!ms) return 0;
                  const d = new Date(ms);
                  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
                };
                const tripEntries = driverShipments.map(s => ({
                  id: `trip-${s.id}`,
                  shipmentId: s.id,
                  type: 'trip',
                  dateMs: getShipmentDateMs(s),
                  createdMs: toMillis(s.createdAt) || getShipmentDateMs(s),
                  amount: commissionByShipment.get(s.id) || 0,
                  description: `${t('drivers.trip', 'Trip')} ${s.shipmentNumber || s.trackingNumber || s.id.slice(0, 6)}`,
                  secondary: [s.clientName, s.origin && s.destination ? `${s.origin} → ${s.destination}` : null].filter(Boolean).join(' • ')
                }));
                const paymentEntries = driverPayments.map(p => ({
                  id: `pay-${p.id}`,
                  paymentId: p.id,
                  type: 'payment',
                  dateMs: toMillis(p.paymentDate) || toMillis(p.createdAt),
                  createdMs: toMillis(p.createdAt) || toMillis(p.paymentDate),
                  amount: Number(p.amount) || 0,
                  description: t('drivers.payment_received', 'Payment'),
                  secondary: [p.method, p.notes].filter(Boolean).join(' • ')
                }));

                // Sort by calendar day, then by actual creation time so entries
                // appear in the order they were recorded (newest at the bottom).
                const ledger = [...tripEntries, ...paymentEntries].sort((a, b) => {
                  const dayDiff = dayKey(a.dateMs) - dayKey(b.dateMs);
                  if (dayDiff !== 0) return dayDiff;
                  return (a.createdMs || 0) - (b.createdMs || 0);
                });
                let running = 0;
                const ledgerWithBalance = ledger.map(e => {
                  running += e.type === 'trip' ? e.amount : -e.amount;
                  return { ...e, balance: running };
                });

                const totalEarned = tripEntries.reduce((s, e) => s + e.amount, 0);
                const totalPaid = paymentEntries.reduce((s, e) => s + e.amount, 0);
                const balance = totalEarned - totalPaid;

                return (
                  <>
                    {/* Summary cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          {t('drivers.total_trip_amount', 'Total trip amount')}
                        </p>
                        <p className="text-2xl font-bold text-gray-900 mt-1">SAR {totalEarned.toFixed(2)}</p>
                      </div>
                      <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          {t('drivers.total_paid', 'Total paid')}
                        </p>
                        <p className="text-2xl font-bold text-green-600 mt-1">SAR {totalPaid.toFixed(2)}</p>
                      </div>
                      <div className={`rounded-lg border p-4 ${balance > 0 ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`}>
                        <p className="text-xs uppercase tracking-wide text-gray-500">
                          {t('drivers.remaining_balance', 'Remaining balance')}
                        </p>
                        <p className={`text-2xl font-bold mt-1 ${balance > 0 ? 'text-red-600' : balance < 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                          SAR {balance.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    {/* Ledger table */}
                    <div className="card">
                      <div className="card-body">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">
                          {t('drivers.ledger', 'Ledger')}
                        </h3>
                        {ledgerLoading || shipmentsLoading ? (
                          <LoadingSpinner />
                        ) : ledgerWithBalance.length === 0 ? (
                          <div className="text-center py-10">
                            <DocumentIcon className="mx-auto h-10 w-10 text-gray-400" />
                            <p className="mt-2 text-sm text-gray-500">
                              {t('drivers.ledger_empty', 'No trips or payments yet')}
                            </p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto -mx-1">
                            <table className="min-w-full">
                              <thead>
                                <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                                  <th className="px-3 py-2 font-medium w-28">{t('common.date', 'Date')}</th>
                                  <th className="px-3 py-2 font-medium">{t('drivers.description', 'Description')}</th>
                                  <th className="px-3 py-2 font-medium w-32">{t('drivers.credit', 'Credit')}</th>
                                  <th className="px-3 py-2 font-medium w-32">{t('drivers.debit', 'Debit')}</th>
                                  <th className="px-3 py-2 font-medium w-32">{t('drivers.balance', 'Balance')}</th>
                                  <th className="px-2 py-2 w-8"></th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {ledgerWithBalance.map(entry => (
                                  <tr
                                    key={entry.id}
                                    className={`text-sm ${entry.type === 'trip' ? 'hover:bg-blue-50 cursor-pointer' : 'hover:bg-gray-50'}`}
                                    onClick={() => {
                                      if (entry.type === 'trip' && entry.shipmentId) {
                                        navigate(`/shipments/${entry.shipmentId}`);
                                      }
                                    }}
                                  >
                                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap tabular-nums">
                                      {entry.dateMs ? new Date(entry.dateMs).toLocaleDateString() : '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                      <div className="flex items-center gap-2">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium shrink-0 ${
                                          entry.type === 'trip' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                                        }`}>
                                          {entry.type === 'trip' ? t('drivers.trip', 'Trip') : t('drivers.payment', 'Payment')}
                                        </span>
                                        <div className="min-w-0">
                                          <div className="font-medium text-gray-900 truncate">{entry.description}</div>
                                          {entry.secondary && (
                                            <div className="text-xs text-gray-500 truncate">{entry.secondary}</div>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap tabular-nums" onClick={(e) => e.stopPropagation()}>
                                      {entry.type === 'trip' ? (
                                        editingCommissionId === entry.shipmentId ? (
                                          <div className="flex items-center gap-1">
                                            <input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              autoFocus
                                              value={editingCommissionValue}
                                              onChange={(e) => setEditingCommissionValue(e.target.value)}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleSaveCommission(entry.shipmentId, selectedDriver?.id);
                                                if (e.key === 'Escape') { setEditingCommissionId(null); setEditingCommissionValue(''); }
                                              }}
                                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                                              disabled={savingCommission}
                                            />
                                            <button
                                              onClick={() => handleSaveCommission(entry.shipmentId, selectedDriver?.id)}
                                              className="text-green-600 hover:text-green-800 text-xs font-semibold"
                                              disabled={savingCommission}
                                            >
                                              {savingCommission ? '...' : t('common.save', 'Save')}
                                            </button>
                                            <button
                                              onClick={() => { setEditingCommissionId(null); setEditingCommissionValue(''); }}
                                              className="text-gray-500 hover:text-gray-700 text-xs"
                                              disabled={savingCommission}
                                            >
                                              {t('common.cancel', 'Cancel')}
                                            </button>
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() => {
                                              setEditingCommissionId(entry.shipmentId);
                                              setEditingCommissionValue(entry.amount > 0 ? String(entry.amount) : '');
                                            }}
                                            className={`hover:underline ${entry.amount > 0 ? 'font-medium text-gray-900' : 'text-blue-600 italic text-xs'}`}
                                            title={t('drivers.set_commission', 'Set commission')}
                                          >
                                            {entry.amount > 0
                                              ? `SAR ${entry.amount.toFixed(2)}`
                                              : t('drivers.commission_pending', 'Commission pending')}
                                          </button>
                                        )
                                      ) : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                                      {entry.type === 'payment' ? (
                                        <span className="font-medium text-green-600">SAR {entry.amount.toFixed(2)}</span>
                                      ) : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap tabular-nums font-semibold text-gray-900">
                                      SAR {entry.balance.toFixed(2)}
                                    </td>
                                    <td className="px-2 py-2">
                                      {entry.type === 'payment' && (
                                        <button
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            if (!window.confirm(t('drivers.delete_payment_confirm', 'Delete this payment?'))) return;
                                            try {
                                              await externalDriverPaymentsService.deletePayment(entry.paymentId);
                                              toast.success(t('drivers.payment_deleted', 'Payment deleted'));
                                              loadDriverPayments(selectedDriver.id);
                                              loadExternalDrivers();
                                            } catch (err) {
                                              console.error(err);
                                              toast.error('Failed to delete payment');
                                            }
                                          }}
                                          className="text-gray-400 hover:text-red-600 transition-colors"
                                          title={t('common.delete', 'Delete')}
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}

              {/* All-driver shipments table (only when viewing all shipments) */}
              {!selectedDriver && (
              <div className="card">
                <div className="card-body">
                  {shipmentsLoading ? (
                    <LoadingSpinner />
                  ) : driverShipments.length === 0 ? (
                    <div className="text-center py-12">
                      <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        {t('drivers.no_shipments_for_driver', 'No shipments found')}
                      </h3>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="table-header w-12">
                              <input
                                type="checkbox"
                                checked={selectedShipments.length === driverShipments.length && driverShipments.length > 0}
                                onChange={handleSelectAll}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </th>
                            {!selectedDriver && <th className="table-header">{t('drivers.driver_name', 'Driver')}</th>}
                            <th className="table-header">{t('drivers.client_name', 'Client')}</th>
                            <th className="table-header">{t('drivers.commission', 'Commission')}</th>
                            <th className="table-header">{t('drivers.route', 'Route')}</th>
                            <th className="table-header">{t('drivers.shipment_date', t('common.date'))}</th>
                            <th className="table-header">{t('drivers.payment_status', 'Payment Status')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {driverShipments.map((shipment) => (
                            <tr key={shipment.id} className="hover:bg-gray-50">
                              <td className="table-cell">
                                <input
                                  type="checkbox"
                                  checked={selectedShipments.includes(shipment.id)}
                                  onChange={() => handleShipmentSelect(shipment.id)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                              </td>
                              {!selectedDriver && (
                                <td className="table-cell">
                                  <div className="text-sm font-medium text-gray-900">{shipment.driverName}</div>
                                  <div className="text-sm text-gray-500">{shipment.externalDriverPhone}</div>
                                </td>
                              )}
                              <td className="table-cell">
                                <div className="text-sm font-medium text-gray-900">{shipment.clientName}</div>
                              </td>
                              <td className="table-cell">
                                <div className="text-sm font-medium text-gray-900">
                                  SAR {(commissionByShipment.get(shipment.id) || 0).toFixed(2)}
                                </div>
                              </td>
                              <td className="table-cell">
                                <div className="text-sm text-gray-900">{shipment.origin} → {shipment.destination}</div>
                              </td>
                              <td className="table-cell">
                                <div className="text-sm text-gray-900">
                                  {new Date(shipment.pickupDate).toLocaleDateString()}
                                </div>
                              </td>
                              <td className="table-cell">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  shipment.paymentStatus === 'paid' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {shipment.paymentStatus === 'paid' ? (
                                    <>
                                      <CheckIcon className="w-3 h-3 mr-1" />
                                      {t('drivers.paid', t('common.paid'))}
                                    </>
                                  ) : (
                                    <>
                                      <XMarkIcon className="w-3 h-3 mr-1" />
                                      {t('drivers.not_paid', 'Not Paid')}
                                    </>
                                  )}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              )}
            </>
          )}
        </>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && selectedDriver && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {t('drivers.record_payment', 'Record Payment')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">{selectedDriver.name} • {selectedDriver.phone}</p>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const amt = parseFloat(paymentForm.amount);
                if (!amt || amt <= 0) {
                  toast.error(t('drivers.invalid_amount', 'Enter a valid amount'));
                  return;
                }
                setSavingPayment(true);
                try {
                  await externalDriverPaymentsService.addPayment({
                    companyId: currentCompany.id,
                    driverId: selectedDriver.id,
                    amount: amt,
                    paymentDate: paymentForm.paymentDate,
                    method: paymentForm.method,
                    notes: paymentForm.notes
                  });
                  toast.success(t('drivers.payment_recorded', 'Payment recorded'));
                  setShowPaymentModal(false);
                  loadDriverPayments(selectedDriver.id);
                  loadExternalDrivers();
                } catch (err) {
                  console.error(err);
                  toast.error('Failed to record payment');
                } finally {
                  setSavingPayment(false);
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="label">{t('drivers.amount', 'Amount')} (SAR) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(p => ({ ...p, amount: e.target.value }))}
                  className="input-field"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="label">{t('drivers.payment_date', 'Payment date')}</label>
                <input
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm(p => ({ ...p, paymentDate: e.target.value }))}
                  className="input-field"
                />
              </div>
              <div>
                <label className="label">{t('drivers.payment_method', 'Method')}</label>
                <select
                  value={paymentForm.method}
                  onChange={(e) => setPaymentForm(p => ({ ...p, method: e.target.value }))}
                  className="input-field"
                >
                  <option value="cash">{t('drivers.method_cash', 'Cash')}</option>
                  <option value="bank_transfer">{t('drivers.method_bank_transfer', 'Bank transfer')}</option>
                  <option value="other">{t('drivers.method_other', 'Other')}</option>
                </select>
              </div>
              <div>
                <label className="label">{t('drivers.notes', 'Notes')}</label>
                <textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm(p => ({ ...p, notes: e.target.value }))}
                  className="input-field"
                  rows={2}
                />
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="btn-secondary"
                  disabled={savingPayment}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={savingPayment}
                >
                  {savingPayment ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Internal Drivers Table - Only show when internal tab is active */}
      {activeTab === 'internal' && (
        <div className="card">
          <div className="card-body">
            {drivers.length === 0 ? (
              <div className="text-center py-12">
                <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('drivers.no_drivers_found')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t('drivers.get_started_first_driver')}</p>
                <div className="mt-6">
                  <button
                    onClick={() => {
                      resetForm();
                      setEditingDriver(null);
                      setIsModalOpen(true);
                    }}
                    className="btn-primary"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    {t('drivers.add_driver')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="table-header">{t('drivers.driver_name')}</th>
                      <th className="table-header">{t('drivers.driver_code', 'Driver Code')}</th>
                      <th className="table-header">{t('common.contact')}</th>
                      <th className="table-header">{t('drivers.favorite_vehicle')}</th>
                      <th className="table-header">{t('drivers.remaining_balance', 'Remaining balance')}</th>
                      <th className="table-header">{t('drivers.status')}</th>
                      <th className="table-header">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredDrivers.map((driver) => {
                      const favoriteVehicle = vehicles.find(v => v.id === driver.favoriteVehicleId);

                      return (
                        <tr key={driver.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/drivers/${driver.id}`)}>
                          <td className="table-cell">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                                <UserIcon className="h-6 w-6 text-gray-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{driver.name}</div>
                                <div className="text-sm text-gray-500">{driver.licenseNumber}</div>
                              </div>
                            </div>
                          </td>
                          <td className="table-cell">
                            <div className="text-sm">
                              {driver.driverCode ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {driver.driverCode}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">
                                  {t('drivers.code_pending', 'Code Pending')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="table-cell">
                            <div className="text-sm text-gray-900">{driver.phone}</div>
                            <div className="text-sm text-gray-500">{driver.email}</div>
                          </td>
                          <td className="table-cell">
                            {favoriteVehicle ? (
                              <div className="flex items-center text-sm">
                                <TruckIcon className="h-4 w-4 mr-1 text-blue-600" />
                                <span>{favoriteVehicle.plateNumber}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400">{t('common.none')}</span>
                            )}
                          </td>
                          <td className="table-cell">
                            {(() => {
                              const bal = internalBalanceMap.get(driver.id) || 0;
                              const cls = bal > 0 ? 'text-red-600' : bal < 0 ? 'text-blue-600' : 'text-gray-700';
                              return (
                                <span className={`text-sm font-semibold ${cls}`}>
                                  SAR {bal.toFixed(2)}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="table-cell">
                            <div className="flex flex-col gap-1">
                              <span className={`status-badge ${driver.status === 'active' ? 'status-badge-success' : 'status-badge-warning'}`}>
                                {t(`drivers.${driver.status}`)}
                              </span>
                              {driver.status === 'active' && driverBusyMap.has(driver.id) && (() => {
                                const busy = driverBusyMap.get(driver.id);
                                const label = busy.shipmentStatus === 'in_transit' ? 'In Transit' : 'Busy';
                                return (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-300 w-fit">
                                    <ExclamationTriangleIcon className="h-3 w-3" />
                                    {label}
                                  </span>
                                );
                              })()}
                            </div>
                          </td>
                          <td className="table-cell">
                            <div className="flex space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(driver);
                                }}
                                className="text-blue-600 hover:text-blue-900"
                                title={t('common.edit')}
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(driver.id);
                                }}
                                className="text-red-600 hover:text-red-900"
                                title={t('common.delete')}
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Driver Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingDriver ? t('drivers.edit_driver') : t('drivers.add_new_driver')}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('drivers.driver_name')} *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="label">{t('drivers.nickname')}</label>
                  <input
                    type="text"
                    name="nickname"
                    value={formData.nickname}
                    onChange={handleInputChange}
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                <label className="label">{t('drivers.phone')} *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="input-field"
                  required
                />
              </div>

              <div>
                <label className="label">{t('drivers.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input-field"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('drivers.license_number')}</label>
                  <input
                    type="text"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleInputChange}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="label">{t('drivers.license_expiry')}</label>
                  <input
                    type="date"
                    name="licenseExpiry"
                    value={formData.licenseExpiry}
                    onChange={handleInputChange}
                    className="input-field"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('drivers.iqamah_number')}</label>
                  <input
                    type="text"
                    name="iqamahNumber"
                    value={formData.iqamahNumber}
                    onChange={handleInputChange}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="label">{t('drivers.iqamah_expiry')}</label>
                  <input
                    type="date"
                    name="iqamahExpiry"
                    value={formData.iqamahExpiry}
                    onChange={handleInputChange}
                    className="input-field"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('drivers.health_insurance_number')}</label>
                  <input
                    type="text"
                    name="healthInsuranceNumber"
                    value={formData.healthInsuranceNumber}
                    onChange={handleInputChange}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="label">{t('drivers.health_insurance_expiry')}</label>
                  <input
                    type="date"
                    name="healthInsuranceExpiry"
                    value={formData.healthInsuranceExpiry}
                    onChange={handleInputChange}
                    className="input-field"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('drivers.favorite_vehicle')}</label>
                  <select
                    name="favoriteVehicleId"
                    value={formData.favoriteVehicleId}
                    onChange={handleInputChange}
                    className="input-field"
                  >
                    <option value="">{t('common.select_option')}</option>
                    {vehicles.map(vehicle => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.plateNumber} - {vehicle.make} {vehicle.model}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">{t('drivers.status')}</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="input-field"
                  >
                    <option value="active">{t('drivers.active')}</option>
                    <option value="inactive">{t('drivers.inactive')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label">{t('drivers.notes')}</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="input-field"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {editingDriver ? t('drivers.edit_driver') : t('drivers.add_driver')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Statement Modal */}
      {showStatementModal && selectedDriver && (() => {
        const tripEntries = driverShipments.map(s => ({
          id: `trip-${s.id}`,
          shipmentId: s.id,
          type: 'trip',
          dateMs: getShipmentDateMs(s),
          createdMs: toMillis(s.createdAt) || getShipmentDateMs(s),
          amount: commissionByShipment.get(s.id) || 0,
          description: `${t('drivers.trip', 'Trip')} ${s.shipmentNumber || s.trackingNumber || s.id.slice(0, 6)}`,
          secondary: [s.clientName, s.origin && s.destination ? `${s.origin} - ${s.destination}` : null].filter(Boolean).join(' | ')
        }));
        const paymentEntries = driverPayments.map(p => ({
          id: `pay-${p.id}`,
          paymentId: p.id,
          type: 'payment',
          dateMs: toMillis(p.paymentDate) || toMillis(p.createdAt),
          createdMs: toMillis(p.createdAt) || toMillis(p.paymentDate),
          amount: Number(p.amount) || 0,
          description: t('drivers.payment_received', 'Payment'),
          secondary: [p.method, p.notes].filter(Boolean).join(' | ')
        }));
        return (
          <DriverStatementModal
            isOpen={showStatementModal}
            onClose={() => setShowStatementModal(false)}
            driver={selectedDriver}
            company={currentCompany}
            tripEntries={tripEntries}
            paymentEntries={paymentEntries}
          />
        );
      })()}

      {/* External driver edit modal */}
      {showExternalDriverModal && editingExternalDriver && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingExternalDriver?.id
                  ? t('drivers.edit_external_driver', 'Edit External Driver')
                  : t('drivers.add_external_driver', 'Add External Driver')}
              </h3>
              <button
                type="button"
                onClick={() => { setShowExternalDriverModal(false); setEditingExternalDriver(null); }}
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                disabled={savingExternalDriver}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleExternalDriverSave} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('drivers.name', 'Name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={externalDriverForm.name}
                  onChange={(e) => setExternalDriverForm(prev => ({ ...prev, name: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={savingExternalDriver}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('drivers.phone', 'Phone')}
                </label>
                <input
                  type="tel"
                  value={externalDriverForm.phone}
                  onChange={(e) => setExternalDriverForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  disabled={savingExternalDriver}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowExternalDriverModal(false); setEditingExternalDriver(null); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={savingExternalDriver}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                  disabled={savingExternalDriver}
                >
                  {savingExternalDriver ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shipment Costing modal (no financial KPIs) */}
      {showCostingModal && (
        <div className="fixed inset-0 z-50 flex flex-col bg-gray-50">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white border-b border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 min-w-0">
              <CalculatorIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {t('accounting.shipment_costing', 'Shipment Costing')}
              </h2>
            </div>
            <button
              onClick={() => setShowCostingModal(false)}
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              title={t('common.close', 'Close')}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 sm:p-4">
            <ShipmentCosting hideMetrics />
          </div>
        </div>
      )}
    </div>
  );
};

export default Drivers;